import React, { useCallback, useEffect, useState, useRef } from "react";
import BadgeIcon from "@mui/icons-material/Badge";
import ViewHeadlineIcon from "@mui/icons-material/ViewHeadline";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import { MAIN_URL } from "../../../Configurations/Urls.js";
import toast from "react-hot-toast";
import TableDataGeneric from "../../../Configurations/TableDataGeneric.js";
import Layout4 from "../../DataLayouts/Layout4.jsx";
import { useNavigate, useSearchParams } from "react-router-dom";
import { operatorRequiresValue } from "../../../Components/filters/operators";

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "organization_person_identification_type_id",
  sortOrder: "desc",
};

const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    )
      return;

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue
            .split(",")
            .map((v) => v.trim().toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      filters.push({ field, operator, value: filterValue });
    }
  });

  const search = searchParams.get("search");
  if (search) {
    filters.push({
      field: "identification_type_name",
      operator: "contains",
      value: search,
    });
  }

  return filters;
};

function IdentificationTypeList() {
  const { userData } = useAuthStore();
  const org = userData?.organization;
  const [loading, setLoading] = useState(true);
  const [identificationTypes, setIdentificationTypes] = useState([]);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);

  // Initialize URL parameters
  useEffect(() => {
    if (hasInitializedURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  // Load data
  useEffect(() => {
    if (!org?.organization_id || !hasInitializedURL.current) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();

    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = buildFiltersFromParams(searchParams);
        const requestBody = {
          filters,
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field:
              searchParams.get("sortBy") ||
              "organization_person_identification_type_id",
            direction: searchParams.get("sortOrder") || "desc",
          },
        };

        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/person-identification-type/filtered`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data = [];
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;

            if (result.pagination) {
              const pageSize =
                result.pagination.pageSize || result.pagination.limit || 10;
              setPaginationData({
                page:
                  result.pagination.page ||
                  Number.parseInt(searchParams.get("page"), 10) ||
                  1,
                pageSize,
                total: result.pagination.total || 0,
                totalPages: result.pagination.totalPages || 1,
                hasNext: result.pagination.hasNext || false,
                hasPrev: result.pagination.hasPrev || false,
              });
            }
          } else {
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const withId = data.map((item) => ({
          ...item,
          id: item.organization_person_identification_type_id,
        }));

        setIdentificationTypes(withId);
      } catch (error) {
        if (error.name === "AbortError") {
          return;
        }
        console.error("Error fetching identification types:", error);
        toast.error("Failed to load identification types");
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [org?.organization_id, searchParams]);

  const handleDelete = useCallback(
    async (id) => {
      try {
        const org_id = org.organization_id;
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org_id}/person-identification-type/${id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (response.ok) {
          const result = await response.json();
          toast.success(
            result.message || "Identification type deleted successfully"
          );
          const params = new URLSearchParams(searchParams);
          setSearchParams(params);
        } else {
          const errorData = await response.json();
          if (response.status === 401) {
            toast.error("Session Expired!");
            window.location.href = "/login";
          } else {
            const errorMessage =
              errorData.message ||
              errorData.errors?.[
                Object.keys(errorData.errors || {})[0]
              ]?.[0] ||
              "Failed to delete identification type";
            toast.error(errorMessage);
          }
        }
      } catch (error) {
        console.error("Delete failed:", error);
        toast.error("Failed to delete identification type");
      }
    },
    [org?.organization_id, searchParams, setSearchParams]
  );

  const handleEdit = useCallback(
    (item) => {
      navigate(
        `/organization-configration/person-identification-type/edit/${item.id}`
      );
    },
    [navigate]
  );

  const handleShow = useCallback(
    (item) => {
      navigate(
        `/organization-configration/person-identification-type/view/${item.id}`
      );
    },
    [navigate]
  );

  return (
    <>
      <Layout4
        loading={loading}
        heading={"Identification Types"}
        btnName={"Add Identification Type"}
        Data={identificationTypes}
        add_action={"LANGUAGE_ADD"}
        delete_action={"IDENTIFICATION_TYPE_DELETE"}
        tableHeaders={[
          {
            name: "Identification Type",
            value_key: "identification_type_name",
            textStyle: "capitalize",
          },
          { name: "Min Length", value_key: "min_length" },
          { name: "Max Length", value_key: "max_length" },
          { name: "Description", value_key: "description" },
        ]}
        Icons={[
          <BadgeIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
          <ViewHeadlineIcon color="primary" />,
          <BadgeIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={[
          "Person Identification Type",
          "Person Identification Type",
          "Add Person Identification Type",
          "Person Identification Type",
        ]}
        Route={"/organization-configration/person-identification-type"}
        setData={setIdentificationTypes}
        DeleteFunc={handleDelete}
      />

      <TableDataGeneric
        tableName="organization_person_identification_types"
        primaryKey="organization_person_identification_type_id"
        heading="Identification Types"
        data={identificationTypes}
        sortname="identification_type_name"
        showActions={true}
        Route="/organization-configration/person-identification-type"
        DeleteFunc={handleDelete}
        EditFunc={handleEdit}
        handleShow={handleShow}
        token={localStorage.getItem("token")}
        edit_delete_action={["LANGUAGE_DELETE", "LANGUAGE_EDIT"]}
        organizationUserId={userData?.organization_user_id}
        showLayoutButtons={true}
        paginationData={paginationData}
        filterModule="organization_person_identification_types"
        config={{
          defaultVisibleColumns: [
            "identification_type_name",
            "min_length",
            "max_length",
            "description",
          ],
          mandatoryColumns: [
            "identification_type_name",
            "min_length",
            "max_length",
            "description",
          ],
        }}
      />
    </>
  );
}

export default IdentificationTypeList;
